/**
 * BossIntroScene - Dramatic intro screen shown before elite/boss battles
 * Displays encounter name, portrait emoji, flavor text with typewriter effect
 */

import Phaser from 'phaser';
import { SCENES, GAME_CONFIG, Z_DEPTHS } from '../constants/GameConstants';
import { T, INK, makeButton } from '../constants/Theme';
import { GameStateManager } from '../game/GameStateManager';
import { ENCOUNTER_DATABASE } from '../game/encounters/EncounterDatabase';

export class BossIntroScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.BOSS_INTRO });
  }

  create(): void {
    const gameStateManager = GameStateManager.getInstance();
    const floorData = gameStateManager.getCurrentFloorData();

    // Look up encounter
    const encounter = floorData.encounterId
      ? ENCOUNTER_DATABASE[floorData.encounterId]
      : null;

    if (!encounter) {
      // Fallback: skip intro and go straight to battle
      this.scene.start(SCENES.BATTLE);
      return;
    }

    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const accentColorStr = '#' + encounter.accentColor.toString(16).padStart(6, '0');

    // Dark vignette background tinted toward the encounter accent
    const bg = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c);
    bg.setOrigin(0);
    bg.setAlpha(0);
    bg.setDepth(0);
    const glow = this.add.rectangle(WIDTH / 2, 360, WIDTH, 360, encounter.accentColor, 0.12);
    glow.setDepth(0);
    glow.setAlpha(0);
    this.tweens.add({ targets: glow, alpha: 0.12, duration: 600, delay: 300 });

    // Fade in background
    this.tweens.add({
      targets: bg,
      alpha: 1,
      duration: 300,
    });

    // Title pulses above ("ELITE ENCOUNTER" / "BOSS FIGHT")
    const title = this.add.text(WIDTH / 2, 180, encounter.title, T.subheading({ color: accentColorStr, fontStyle: '700' }));
    title.setOrigin(0.5);
    title.setLetterSpacing(8);
    title.setAlpha(0);
    title.setDepth(Z_DEPTHS.UI);

    this.time.delayedCall(400, () => {
      title.setAlpha(1);
      this.tweens.add({
        targets: title,
        alpha: 0.5,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    // Large emoji portrait bounces in from top
    const portrait = this.add.text(WIDTH / 2, -100, encounter.portraitEmoji, {
      fontSize: '120px',
    });
    portrait.setOrigin(0.5);
    portrait.setDepth(Z_DEPTHS.UI);

    this.tweens.add({
      targets: portrait,
      y: 360,
      duration: 600,
      delay: 300,
      ease: 'Bounce.easeOut',
    });

    // Encounter name slides in from left
    const nameText = this.add.text(-300, 480, encounter.name, T.display({ color: accentColorStr, fontSize: '52px' }));
    nameText.setOrigin(0.5);
    nameText.setShadow(0, 4, '#0a0f1c', 10, false, true);
    nameText.setDepth(Z_DEPTHS.UI);

    this.tweens.add({
      targets: nameText,
      x: WIDTH / 2,
      duration: 500,
      delay: 600,
      ease: 'Power2',
    });

    // Flavor text with typewriter effect
    const flavorText = this.add.text(WIDTH / 2, 580, '', T.body({
      color: INK.secondary, wordWrap: { width: WIDTH - 110 }, align: 'center', lineSpacing: 8,
    }));
    flavorText.setOrigin(0.5, 0);
    flavorText.setDepth(Z_DEPTHS.UI);

    const fullText = encounter.flavor;
    let charIndex = 0;
    const typewriterDelay = 1200; // Wait for animations to finish

    this.time.delayedCall(typewriterDelay, () => {
      const typeEvent = this.time.addEvent({
        delay: 35,
        repeat: fullText.length - 1,
        callback: () => {
          charIndex++;
          flavorText.setText(fullText.substring(0, charIndex));
        },
      });

      // Show ENGAGE button after text finishes (with minimum 3s from scene start)
      const textDuration = fullText.length * 35;
      const minWait = Math.max(3000 - typewriterDelay, textDuration);

      this.time.delayedCall(minWait + 200, () => {
        // Stop typewriter if still running and show full text
        typeEvent.destroy();
        flavorText.setText(fullText);
        this.showEngageButton(accentColorStr, encounter.accentColor);
      });
    });
  }

  private showEngageButton(_accentColorStr: string, accent: number): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const btn = makeButton(this, WIDTH / 2, HEIGHT - 150, 'ENGAGE', {
      width: 340, fill: accent, depth: Z_DEPTHS.UI,
      onClick: () => {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start(SCENES.BATTLE));
      },
    });
    btn.container.setAlpha(0);
    this.tweens.add({ targets: btn.container, alpha: 1, duration: 300 });
  }
}
